import { useState, useEffect } from 'react';

import { useAuthorizeSigningRequest } from '@/providers';
import type { SponsorshipMode } from '@infinex-private/connect-types';
import { ChevronDownIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import {
  Text,
  ContextualHelp,
  Skeleton,
  SkeletonText,
  SkeletonMedia,
} from '@infinex/ui/components';

import { InsufficientFundsWarning } from './InsufficientFundsWarning';
import { PaymentMethodButton } from './PaymentMethodButton';
import { PaymentMethodModal } from './PaymentMethodModal';

import type { UserNativeBalanceDetails } from '../../types';

type GasDetailsSectionProps = {
  userBalance?: UserNativeBalanceDetails;
  isLoadingBalance: boolean;
  gasAccountBalance: number | null;
  formattedGasCost: string;
  isHighGasCost: boolean;
  nativeBalanceUsd: number;
  formattedNativeBalance: string;
  hasSufficientFunds: boolean;
  onTopUpGasAccount?: () => void;
};

export function GasDetailsSection({
  userBalance,
  isLoadingBalance,
  gasAccountBalance,
  formattedGasCost,
  isHighGasCost,
  nativeBalanceUsd,
  formattedNativeBalance,
  hasSufficientFunds,
  onTopUpGasAccount,
}: GasDetailsSectionProps) {
  const { metadata, selectedSponsorshipMode, setSelectedSponsorshipMode } =
    useAuthorizeSigningRequest();
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] =
    useState(false);
  const [hasAutoSelectedDefault, setHasAutoSelectedDefault] = useState(false);

  // Auto-select payment method based on highest balance (once balances are loaded)
  useEffect(() => {
    if (isLoadingBalance || hasAutoSelectedDefault) return;
    if (!metadata?.supportedSponsorshipModes) return;

    const supported = metadata.supportedSponsorshipModes;
    const isAccountSupported = supported.includes('account');
    const isNativeSupported = supported.includes('native');

    // If only one option is supported, use that
    if (isAccountSupported && !isNativeSupported) {
      setSelectedSponsorshipMode('account');
    } else if (isNativeSupported && !isAccountSupported) {
      setSelectedSponsorshipMode('native');
    } else if (isAccountSupported && isNativeSupported) {
      // Both supported: prefer gas account if it has any balance
      const accountBalance = gasAccountBalance ?? 0;
      if (accountBalance > 0) {
        setSelectedSponsorshipMode('account');
      } else {
        setSelectedSponsorshipMode('native');
      }
    }

    setHasAutoSelectedDefault(true);
  }, [
    isLoadingBalance,
    hasAutoSelectedDefault,
    metadata?.supportedSponsorshipModes,
    gasAccountBalance,
    nativeBalanceUsd,
    setSelectedSponsorshipMode,
  ]);

  const handlePaymentMethodSelect = (method: SponsorshipMode) => {
    setSelectedSponsorshipMode(method);
    setIsPaymentMethodModalOpen(false);
  };

  // Show skeleton while loading balance data
  if (isLoadingBalance) {
    return (
      <div className="space-y-3 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Text className="text-secondary text-sm font-medium">
              Network cost
            </Text>
            <Skeleton>
              <div className="w-16">
                <SkeletonText size="sm" width={1} />
              </div>
            </Skeleton>
          </div>

          <Skeleton>
            <div className="bg-surfaceTwo flex items-center gap-1 rounded-full px-2.5 py-1.5">
              <SkeletonMedia shape="circle" height={4} aspectRatio="1/1" />
              <div className="w-16">
                <SkeletonText size="sm" width={1} />
              </div>
              <ChevronDownIcon size="xsmall" className="text-disabled" />
            </div>
          </Skeleton>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3 py-2">
        <div className="flex items-center justify-between">
          {/* Left side - Network cost text and contextual help */}
          <div className="flex items-center gap-1">
            <Text
              className={cn(
                'text-sm font-medium',
                isHighGasCost ? 'text-caution' : 'text-secondary'
              )}
            >
              Network cost{' '}
              {formattedGasCost.startsWith('<')
                ? `<$${formattedGasCost.slice(1)}`
                : `~$${formattedGasCost}`}
            </Text>
            <ContextualHelp variant="info" className="fill-tertiary" size="sm">
              <div className="text-default grid gap-2 p-4 text-sm">
                {isHighGasCost && (
                  <p className="font-semibold">
                    Network costs are higher than usual.
                  </p>
                )}
                <p>
                  {selectedSponsorshipMode === 'account' ? (
                    <>
                      Network costs are being paid from your Gas Account. You
                      have ${gasAccountBalance?.toFixed(2) || '0.00'} credit.
                    </>
                  ) : (
                    <>
                      Gas fees are paid from your {userBalance?.symbol} balance.
                      Your current balance is {formattedNativeBalance}{' '}
                      {userBalance?.symbol}.
                    </>
                  )}
                </p>
              </div>
            </ContextualHelp>
          </div>

          {/* Right side - Payment method button */}
          <PaymentMethodButton
            selectedPaymentMethod={selectedSponsorshipMode}
            userBalance={userBalance}
            onClick={() => setIsPaymentMethodModalOpen(true)}
          />
        </div>

        {/* Insufficient funds warning */}
        {!hasSufficientFunds && (
          <InsufficientFundsWarning
            selectedPaymentMethod={selectedSponsorshipMode}
            userBalance={userBalance}
            onTopUpGasAccount={onTopUpGasAccount}
          />
        )}

        {/* Payment method selection modal */}
        <PaymentMethodModal
          isOpen={isPaymentMethodModalOpen}
          onClose={() => setIsPaymentMethodModalOpen(false)}
          selectedPaymentMethod={selectedSponsorshipMode}
          gasAccountBalance={gasAccountBalance}
          userBalance={userBalance}
          supportedSponsorshipModes={metadata?.supportedSponsorshipModes}
          onSelectPaymentMethod={handlePaymentMethodSelect}
        />
      </div>
    </>
  );
}
